import React, { useState, useEffect } from 'react';
import { toast } from 'react-toastify';
import Pagination from '../../components/Pagination';
import { postRequest } from "../../utils/networking";
import { apiUrls } from "../../utils/url";
import NoDataFound from '../../components/NoDataFound';
import CommonToastContainer from '../../components/CommonToastContainer';
import { Search, X } from 'lucide-react';
import ServiceLogsPopup from '../../components/ServiceLogsPopup';
import UpdateStatusModal from '../../components/UpdateStatusModal';

// Service type mapping
const getServiceTypeName = (servicetype) => {
  const serviceTypes = {
    1: 'Web Development',
    2: 'Mobile App Development',
    3: 'UI/UX Design',
    4: 'Digital Marketing',
    5: 'SEO Services',
    6: 'Cloud Services',
    7: 'IT Consulting',
    8: 'Software Testing',
    9: 'Database Management',
    10: 'E-commerce Solutions',
  };
  return serviceTypes[servicetype] || `Service Type ${servicetype}`;
};

const getStatusBadgeClasses = (currentstatus) => {
  const status = Number(currentstatus);
  switch (status) {
    case 0:
      return 'bg-amber-50 text-amber-700';
    case 1:
      return 'bg-sky-50 text-sky-700';
    case 2:
      return 'bg-emerald-50 text-emerald-700';
    case 3:
      return 'bg-blue-50 text-blue-700';
    default:
      return 'bg-gray-50 text-gray-700';
  }
};

const getStatusText = (currentstatus) => {
  const status = Number(currentstatus);
  switch (status) {
    case 0:
      return 'Pending';
    case 1:
      return 'In Progress';
    case 2:
      return 'Completed';
    case 3:
      return 'Re-Opened';
    default:
      return 'Pending';
  }
};

const getStatusIcon = (currentstatus) => {
  const status = Number(currentstatus);
  switch (status) {
    case 0:
      return '⏳';
    case 1:
      return '🔄';
    case 2:
      return '✅';
    case 3:
      return '🔓';
    default:
      return '⏳';
  }
};

// TableRow component - without checkbox
const TableRow = React.memo(({ 
  registration, 
  index, 
  onReOpen,
  onViewLogs,
  isUpdating 
}) => {
  const currentStatusValue = Number(registration.currentstatus);
  const statusBadgeClasses = getStatusBadgeClasses(currentStatusValue);
  const statusText = getStatusText(currentStatusValue);
  const statusIcon = getStatusIcon(currentStatusValue);
  
  const serviceName = getServiceTypeName(registration.servicetype);

  const handleRowClick = (e) => {
    if (e.target.closest('.reopen-button')) return;
    onViewLogs(registration);
  };

  const handleReOpenClick = (e) => {
    e.stopPropagation();
    onReOpen(registration);
  };

  return (
    <div 
      className={`grid grid-cols-[60px_1.2fr_1.2fr_1.8fr_1.2fr_1fr_0.8fr] min-w-[1000px] gap-3 py-4 px-6 transition-all duration-200 border-b border-zinc-100 cursor-pointer hover:bg-zinc-100`}
      onClick={handleRowClick}
    >
      <div className="flex items-center">
        <span className="text-sm font-semibold text-zinc-800">
          {index + 1}
        </span>
      </div>
      
      <div className="flex items-center min-w-0">
        <span className="font-medium text-sm text-zinc-900 truncate" title={serviceName}>
          {serviceName}
        </span>
      </div>
      
      <div className="flex items-center min-w-0">
        <span className="font-medium text-sm text-zinc-900 truncate" title={registration.username || registration.name}>
          {registration.username || registration.name || '-'}
        </span>
      </div>
      
      <div className="flex items-center min-w-0">
        <span className="text-sm text-zinc-900 truncate" title={registration.email}>
          {registration.email || '-'}
        </span>
      </div>
      
      <div className="flex items-center">
        <span className="text-sm text-zinc-700 font-mono">{registration.phonenumber || '-'}</span>
      </div>
      
      <div className="flex items-center">
        <span className={`status-badge px-2.5 py-1 rounded-full text-xs font-semibold inline-block whitespace-nowrap ${statusBadgeClasses}`}>
          {statusIcon} {statusText}
        </span>
      </div>
      
      <div className="flex items-center justify-center reopen-button" onClick={(e) => e.stopPropagation()}>
        <button
          onClick={handleReOpenClick}
          disabled={isUpdating}
          className="bg-blue-50 text-blue-700 px-3 py-1.5 rounded-full text-sm font-medium hover:bg-blue-100 transition-colors flex items-center gap-1 whitespace-nowrap disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {isUpdating ? (
            <>
              <div className="inline-block h-3 w-3 animate-spin rounded-full border-2 border-solid border-blue-600 border-r-transparent"></div>
              <span>Re-opening...</span>
            </>
          ) : (
            <>Re-Open</>
          )}
        </button>
      </div>
    </div>
  );
});

const Service = () => {
  const [registrations, setRegistrations] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(15);
  const [searchTerm, setSearchTerm] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  const [updatingRcids, setUpdatingRcids] = useState(new Set());
  
  // Modal states
  const [showStatusModal, setShowStatusModal] = useState(false);
  const [modalData, setModalData] = useState(null);
  const [isUpdatingStatus, setIsUpdatingStatus] = useState(false);

  // State for service logs popup
  const [showLogsPopup, setShowLogsPopup] = useState(false);
  const [serviceLogs, setServiceLogs] = useState([]);
  const [isLoadingLogs, setIsLoadingLogs] = useState(false);
  const [selectedService, setSelectedService] = useState(null);

  // Fetch service logs
  const fetchServiceLogs = async (rcid) => {
    setIsLoadingLogs(true);
    setServiceLogs([]);
    
    try {
      const response = await postRequest(apiUrls.getservicelogs, { rcid: rcid });
      
      if (response.status === "S" && response.data) {
        if (Array.isArray(response.data)) {
          setServiceLogs(response.data);
        } else if (response.data.logs && Array.isArray(response.data.logs)) {
          setServiceLogs(response.data.logs);
        } else {
          setServiceLogs([]);
        }
      } else {
        setServiceLogs([]);
      }
    } catch (error) {
      console.error("Error fetching service logs:", error);
      setServiceLogs([]);
    } finally {
      setIsLoadingLogs(false);
    }
  };

  // Handle view logs when row is clicked
  const handleViewLogs = async (registration) => {
    setSelectedService(registration);
    setShowLogsPopup(true);
    await fetchServiceLogs(registration.rcid);
  };

  // Fetch completed services data
  const fetchCompletedServices = async () => {
    setIsLoading(true);
    try {
      const response = await postRequest(apiUrls.getcompletedservices, {});
      
      if (response.status === "S" && response.data && Array.isArray(response.data)) {
        const mappedData = response.data.map(service => ({
          rcid: service.rcid || service.id,
          username: service.username || service.name,
          servicename: service.servicename || service.username || service.name,
          servicetype: service.servicetype || 1,
          email: service.email,
          phonenumber: service.phonenumber || service.phone,
          currentstatus: service.currentstatus !== undefined ? service.currentstatus : 2
        }));
        setRegistrations(mappedData);
      } else {
        setRegistrations([]);
      }
    } catch (error) {
      console.error("Error fetching completed services:", error);
      setRegistrations([]);
    } finally {
      setIsLoading(false);
    }
  };

  // Handle re-open click (trigger modal)
  const handleReOpen = (registration) => {
    setModalData({
      rcid: registration.rcid,
      newStatus: 3 // Re-Opened status
    });
    setShowStatusModal(true);
  };

  // Handle final update from modal
  const handleFinalUpdate = async (rcid, givenstatus, remark) => {
    setIsUpdatingStatus(true);
    const loadingToast = toast.loading('Re-opening service...');
    
    try {
      const requestPayload = {
        rcid: rcid,
        givenstatus: givenstatus,
        description: remark
      };
      
      const response = await postRequest(apiUrls.updateserviceregister, requestPayload);
      
      toast.dismiss(loadingToast);
      
      if (response.status === "S") {
        toast.success(response.message || "Service re-opened successfully!");
        setShowStatusModal(false);
        setModalData(null);
        
        // Remove from current list (since it's no longer 'completed')
        setRegistrations(prev => prev.filter(reg => reg.rcid !== rcid));
        await fetchCompletedServices();
      } else {
        toast.error(response.message || "Failed to re-open service");
      }
    } catch (error) {
      toast.dismiss(loadingToast);
      console.error("Error re-opening service:", error);
      toast.error(error.message || "Unable to re-open service. Please try again.");
    } finally {
      setIsUpdatingStatus(false);
    }
  };

  const handleSearchChange = (e) => {
    setSearchTerm(e.target.value);
    setCurrentPage(1);
  };

  const clearSearch = () => {
    setSearchTerm('');
    setCurrentPage(1);
  };

  useEffect(() => {
    fetchCompletedServices();
  }, []);

  const filteredRegistrations = registrations.filter(reg => {
    if (searchTerm) {
      const searchLower = searchTerm.toLowerCase();
      const serviceTypeName = getServiceTypeName(reg.servicetype).toLowerCase();
      const statusText = getStatusText(reg.currentstatus).toLowerCase();
      return (reg.username && reg.username.toLowerCase().includes(searchLower)) || 
             (reg.email && reg.email.toLowerCase().includes(searchLower)) ||
             (reg.servicename && reg.servicename.toLowerCase().includes(searchLower)) ||
             (reg.phonenumber && reg.phonenumber.toLowerCase().includes(searchLower)) ||
             serviceTypeName.includes(searchLower) ||
             statusText.includes(searchLower);
    }
    return true;
  });

  const totalItems = filteredRegistrations.length;
  const totalPages = Math.ceil(totalItems / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const currentRegistrations = filteredRegistrations.slice(startIndex, endIndex);

  const handlePageChange = (page) => {
    setCurrentPage(page);
    const tableContainer = document.querySelector('.table-scroll-container');
    if (tableContainer) {
      tableContainer.scrollTop = 0;
    }
  };

  const handleItemsPerPageChange = (newItemsPerPage) => {
    setItemsPerPage(newItemsPerPage);
    setCurrentPage(1);
  };

  return (
    <div className="bg-[#F8F9FE] text-zinc-900 antialiased h-full overflow-hidden">
      <main className="max-w-[1400px] mx-auto h-full flex flex-col p-2 sm:p-3 lg:px-4 lg:py-6 w-full">
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mb-6">
          <div className="relative w-full sm:w-80">
            <input
              type="text"
              placeholder="Search by name, email or phone..."
              value={searchTerm}
              onChange={handleSearchChange}
              className="w-full px-4 py-2 pl-10 pr-10 text-sm border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white"
            />
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
            {searchTerm && (
              <button
                onClick={clearSearch}
                className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600 transition-colors cursor-pointer"
                aria-label="Clear search"
              >
                <X className="w-4 h-4" />
              </button>
            )}
          </div>
        </div>
        
        <div className="bg-white/70 rounded-xl border border-zinc-100 shadow-sm flex-1 flex flex-col min-h-0 overflow-hidden">
          <div className="overflow-auto flex-1 flex flex-col min-h-0">
            <div className="sticky top-0 z-10 bg-gradient-to-r from-zinc-100 to-zinc-50 border-b border-zinc-200 min-w-[1000px]">
              <div className="grid grid-cols-[60px_1.2fr_1.2fr_1.8fr_1.2fr_1fr_0.8fr] gap-3 px-6 py-3 text-sm font-semibold text-zinc-600">
                <div className="whitespace-nowrap">S.No</div>
                <div className="whitespace-nowrap">Service Name</div>
                <div className="whitespace-nowrap">User Name</div>
                <div className="whitespace-nowrap">User Email</div>
                <div className="whitespace-nowrap">Phone</div>
                <div className="whitespace-nowrap">Status</div>
                <div className="whitespace-nowrap text-center">Action</div>
              </div>
            </div>

            <div className="table-scroll-container flex-1 relative min-h-[400px]">
              {isLoading ? (
                <div className="absolute inset-0 flex items-center justify-center bg-white/50">
                  <div className="inline-block h-12 w-12 animate-spin rounded-full border-4 border-solid border-amber-600 border-r-transparent align-[-0.125em] motion-reduce:animate-[spin_1.5s_linear_infinite]"></div>
                </div>
              ) : currentRegistrations.length > 0 ? (
                currentRegistrations.map((registration, index) => (
                  <TableRow
                    key={registration.rcid}
                    registration={registration}
                    index={startIndex + index}
                    onReOpen={handleReOpen}
                    onViewLogs={handleViewLogs}
                    isUpdating={updatingRcids.has(registration.rcid)}
                  />
                ))
              ) : (
                <NoDataFound searchTerm={searchTerm} />
              )}
            </div>
          </div>

          {totalItems > 0 && !isLoading && (
            <div className="mt-auto border-t border-zinc-100">
              <Pagination
                currentPage={currentPage}
                totalPages={totalPages}
                itemsPerPage={itemsPerPage}
                totalItems={totalItems}
                onPageChange={handlePageChange}
                onItemsPerPageChange={handleItemsPerPageChange}
              />
            </div>
          )}
        </div>
      </main>
      
      <ServiceLogsPopup
        isOpen={showLogsPopup}
        onClose={() => {
          setShowLogsPopup(false);
          setSelectedService(null);
          setServiceLogs([]);
        }}
        logs={serviceLogs}
        isLoading={isLoadingLogs}
        serviceName={selectedService ? getServiceTypeName(selectedService.servicetype) : ''}
        userName={selectedService ? (selectedService.username || selectedService.name || 'User') : ''}
      />
      
      <UpdateStatusModal
        isOpen={showStatusModal}
        onClose={() => {
          setShowStatusModal(false);
          setModalData(null);
        }}
        data={modalData}
        onUpdate={handleFinalUpdate}
        isUpdating={isUpdatingStatus}
      />
      
      <CommonToastContainer />
    </div>
  );
};

export default Service;